import { useState, useEffect } from "react";
import axios from "axios";
import {
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Filter,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  ArrowLeftRight,
  ChevronDown,
  ChevronRight,
  Trash2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

// Build backend URL
let BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (BACKEND_URL && !BACKEND_URL.startsWith('http')) {
  BACKEND_URL = `https://${BACKEND_URL}`;
}
if (!BACKEND_URL) {
  BACKEND_URL = window.location.origin;
}
const API = `${BACKEND_URL}/api`;

export default function SyncHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [objectMappings, setObjectMappings] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    fetchObjectMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchObjectMappings = async () => {
    try {
      const response = await axios.get(`${API}/object-mappings`);
      setObjectMappings(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setObjectMappings([]);
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?object_type=${filter}&limit=100` : "?limit=100";
      const response = await axios.get(`${API}/sync/history${params}`);
      setHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch history:", error);
      toast.error("Erreur lors du chargement de l'historique");
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const formatObjectType = (objectType) => {
    if (!objectType) return "-";
    const parts = objectType.split("_", 2);
    if (parts.length === 2) {
      // Find matching object mapping for nice labels
      const mapping = objectMappings.find(
        m => m.hubspot_object === parts[0] && m.bc_entity === parts[1]
      );
      if (mapping) {
        return `${mapping.hubspot_object_label} ↔ ${mapping.bc_entity_label}`;
      }
      // Fallback: capitalize parts
      return `${parts[0]} ↔ ${parts[1]}`;
    }
    return objectType;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "success":
        return (
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Succès
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-rose-50 text-rose-700 border border-rose-200">
            <XCircle className="w-3 h-3 mr-1" />
            Échec
          </Badge>
        );
      case "partial":
        return (
          <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
            <AlertCircle className="w-3 h-3 mr-1" />
            Partiel
          </Badge>
        );
      case "in_progress":
        return (
          <Badge className="bg-blue-50 text-blue-700 border border-blue-200">
            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
            En cours
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getDirectionIcon = (direction) => {
    switch (direction) {
      case "hubspot_to_bc":
        return <ArrowRight className="w-4 h-4 text-slate-400" />;
      case "bc_to_hubspot":
        return <ArrowLeft className="w-4 h-4 text-slate-400" />;
      default:
        return <ArrowLeftRight className="w-4 h-4 text-slate-400" />;
    }
  };

  const getDirectionLabel = (direction) => {
    switch (direction) {
      case "hubspot_to_bc": return "HubSpot → BC";
      case "bc_to_hubspot": return "BC → HubSpot";
      default: return "Bidirectionnel";
    }
  };

  const getFieldStatusBadge = (status) => {
    switch (status) {
      case "ok":
        return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">Synchronisé</Badge>;
      case "hubspot_only":
        return <Badge className="bg-orange-50 text-orange-700 border border-orange-200 text-[10px]">HS seul</Badge>;
      case "bc_only":
        return <Badge className="bg-teal-50 text-teal-700 border border-teal-200 text-[10px]">BC seul</Badge>;
      case "no_data":
        return <Badge className="bg-slate-50 text-slate-500 border border-slate-200 text-[10px]">Pas de données</Badge>;
      default:
        return <Badge className="bg-slate-50 text-slate-500 border border-slate-200 text-[10px]">Vide</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const formatDuration = (started, completed) => {
    if (!started || !completed) return "-";
    const diff = (new Date(completed) - new Date(started)) / 1000;
    if (diff < 60) return `${diff.toFixed(1)}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ${Math.floor(diff % 60)}s`;
    return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
  };

  const hasFieldDetails = (sync) => sync.details?.field_results?.length > 0;

  const handlePurgeHistory = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer tout l'historique de synchronisation ?")) {
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${API}/sync/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "Historique supprimé");
      fetchHistory();
    } catch (error) {
      toast.error("Erreur: " + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sync-history-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">
            Historique des Synchronisations
          </h1>
          <p className="mt-1 text-slate-500">
            Consultez l'historique et les logs de vos synchronisations
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="filter-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les objets</SelectItem>
              {objectMappings.map((m) => {
                const key = `${m.hubspot_object}_${m.bc_entity}`;
                return (
                  <SelectItem key={key} value={key}>
                    {m.hubspot_object_label} ↔ {m.bc_entity_label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchHistory} data-testid="refresh-history-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button
            variant="outline"
            onClick={handlePurgeHistory}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Purger
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100">
                <Clock className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-slate-900">{history.length}</p>
                <p className="text-xs text-slate-500">Total Syncs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-50">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-emerald-600">
                  {history.filter(h => h.status === "success").length}
                </p>
                <p className="text-xs text-slate-500">Réussies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-rose-50">
                <XCircle className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-rose-600">
                  {history.filter(h => h.status === "failed").length}
                </p>
                <p className="text-xs text-slate-500">Échouées</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-50">
                <RefreshCw className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-indigo-600">
                  {history.reduce((sum, h) => sum + (h.records_synced || 0), 0)}
                </p>
                <p className="text-xs text-slate-500">Records analysés</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* History Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Journal des Synchronisations</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {history.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Date/Heure</TableHead>
                  <TableHead>Mapping</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Records</TableHead>
                  <TableHead className="text-right">Durée</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((sync) => {
                  const isExpanded = expandedRow === sync.id;
                  const hasDetails = hasFieldDetails(sync);
                  return (
                    <>
                      <TableRow
                        key={sync.id}
                        className={hasDetails ? "cursor-pointer hover:bg-slate-50" : ""}
                        onClick={() => hasDetails && setExpandedRow(isExpanded ? null : sync.id)}
                        data-testid={`history-row-${sync.id}`}
                      >
                        <TableCell className="w-8 px-2">
                          {hasDetails && (
                            isExpanded
                              ? <ChevronDown className="w-4 h-4 text-slate-400" />
                              : <ChevronRight className="w-4 h-4 text-slate-400" />
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-slate-600">
                          {formatDate(sync.started_at)}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium">
                            {formatObjectType(sync.object_type)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`text-xs font-medium ${sync.sync_type === 'manual' ? 'text-indigo-600' : 'text-slate-600'}`}>
                            {sync.sync_type === 'manual' ? 'Manuel' : 'Planifié'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {getDirectionIcon(sync.direction)}
                            <span className="text-xs text-slate-500">
                              {getDirectionLabel(sync.direction)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(sync.status)}
                        </TableCell>
                        <TableCell className="text-right">
                          {sync.details?.fields_checked ? (
                            <div className="text-right">
                              <span className="text-sm font-medium text-emerald-600">
                                {sync.details.fields_ok}/{sync.details.fields_checked}
                              </span>
                              <span className="text-xs text-slate-400 ml-1">champs OK</span>
                            </div>
                          ) : (
                            <span className="text-sm font-medium text-slate-400">
                              {sync.records_synced || 0}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-slate-500">
                          {formatDuration(sync.started_at, sync.completed_at)}
                        </TableCell>
                      </TableRow>
                      {/* Expanded field detail */}
                      {isExpanded && hasDetails && (
                        <TableRow key={`${sync.id}-detail`}>
                          <TableCell colSpan={8} className="bg-slate-50/50 p-0">
                            <div className="px-6 py-4 space-y-2">
                              <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
                                <span>{sync.details.hubspot_records} records HubSpot</span>
                                <span>{sync.details.bc_records} records BC</span>
                                {sync.details.records_synced !== undefined ? (
                                  <>
                                    {sync.details.records_updated > 0 && <span className="text-blue-600 font-medium">{sync.details.records_updated} mis à jour</span>}
                                    {sync.details.records_created > 0 && <span className="text-emerald-600 font-medium">{sync.details.records_created} créés</span>}
                                    {!sync.details.records_updated && !sync.details.records_created && <span className="text-emerald-600 font-medium">{sync.details.records_synced} synchronisés</span>}
                                    {sync.details.records_failed > 0 && <span className="text-red-600">{sync.details.records_failed} erreurs</span>}
                                    {sync.details.records_skipped > 0 && <span>{sync.details.records_skipped} ignorés</span>}
                                  </>
                                ) : (
                                  <span>{sync.details.fields_ok}/{sync.details.fields_checked} champs avec données des deux côtés</span>
                                )}
                              </div>
                              <div className="grid gap-2">
                                {sync.details.field_results.map((field, i) => (
                                  <div key={i} className="flex items-center gap-3 p-2.5 rounded-md border border-slate-200 bg-white">
                                    {/* HubSpot field */}
                                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                      <div className="w-2 h-2 rounded-full bg-[#FF7A59] shrink-0" />
                                      <span className="text-sm truncate">{field.hubspot_field_label}</span>
                                      <span className="text-[10px] text-slate-400 font-mono truncate">{field.hubspot_field}</span>
                                      {field.written_to_hs !== undefined ? (
                                        <Badge variant="outline" className="text-[10px] shrink-0 ml-auto">
                                          {field.written_to_hs > 0 ? `${field.written_to_hs} écrit` : '-'}
                                        </Badge>
                                      ) : (
                                        <Badge variant="outline" className="text-[10px] shrink-0 ml-auto">
                                          {field.hubspot_count}
                                        </Badge>
                                      )}
                                    </div>
                                    {/* Direction */}
                                    <div className="shrink-0 flex items-center gap-1 text-slate-400">
                                      {field.direction === "hubspot_to_bc" ? (
                                        <ArrowRight className="w-3.5 h-3.5" />
                                      ) : field.direction === "bc_to_hubspot" ? (
                                        <ArrowLeft className="w-3.5 h-3.5" />
                                      ) : (
                                        <ArrowLeftRight className="w-3.5 h-3.5" />
                                      )}
                                    </div>
                                    {/* BC field */}
                                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                      {field.written_to_bc !== undefined ? (
                                        <Badge variant="outline" className="text-[10px] shrink-0 mr-auto">
                                          {field.written_to_bc > 0 ? `${field.written_to_bc} écrit` : '-'}
                                        </Badge>
                                      ) : (
                                        <Badge variant="outline" className="text-[10px] shrink-0 mr-auto">
                                          {field.bc_count}
                                        </Badge>
                                      )}
                                      <span className="text-sm truncate">{field.bc_field_label}</span>
                                      <span className="text-[10px] text-slate-400 font-mono truncate">{field.bc_field}</span>
                                      <div className="w-2 h-2 rounded-full bg-[#00B7C3] shrink-0" />
                                    </div>
                                    {/* Status */}
                                    <div className="shrink-0">
                                      {getFieldStatusBadge(field.status)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {/* Sample values */}
                              {sync.details.field_results.some(f => f.hubspot_sample?.length > 0 || f.bc_sample?.length > 0) && (
                                <details className="mt-2">
                                  <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                                    Voir les valeurs d'exemple
                                  </summary>
                                  <div className="mt-2 grid gap-2">
                                    {sync.details.field_results.filter(f => f.hubspot_sample?.length > 0 || f.bc_sample?.length > 0).map((field, i) => (
                                      <div key={i} className="grid grid-cols-[1fr_1fr] gap-3 p-2 rounded border border-slate-100 text-xs">
                                        <div>
                                          <p className="text-slate-500 mb-1 font-medium">{field.hubspot_field_label}</p>
                                          {field.hubspot_sample?.map((v, j) => (
                                            <p key={j} className="font-mono bg-orange-50 px-2 py-0.5 rounded truncate mb-0.5">{String(v)}</p>
                                          ))}
                                          {(!field.hubspot_sample || field.hubspot_sample.length === 0) && (
                                            <p className="text-slate-300 italic">Aucune donnée</p>
                                          )}
                                        </div>
                                        <div>
                                          <p className="text-slate-500 mb-1 font-medium">{field.bc_field_label}</p>
                                          {field.bc_sample?.map((v, j) => (
                                            <p key={j} className="font-mono bg-teal-50 px-2 py-0.5 rounded truncate mb-0.5">{String(v)}</p>
                                          ))}
                                          {(!field.bc_sample || field.bc_sample.length === 0) && (
                                            <p className="text-slate-300 italic">Aucune donnée</p>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}
                              {/* Error message */}
                              {sync.error_message && (
                                <div className="mt-2 p-2 rounded bg-rose-50 border border-rose-200 text-xs text-rose-700">
                                  {sync.error_message}
                                </div>
                              )}
                              {/* First errors from record details */}
                              {sync.details?.first_errors?.length > 0 && (
                                <div className="mt-2 p-2 rounded bg-red-50 border border-red-200 space-y-1">
                                  <p className="text-xs font-medium text-red-700">Erreurs rencontrées :</p>
                                  {sync.details.first_errors.map((err, j) => (
                                    <p key={j} className="text-xs text-red-600 font-mono break-all">{err}</p>
                                  ))}
                                </div>
                              )}
                              {/* Audit Report Section */}
                              {sync.details?.audit && (
                                <div className="mt-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50/50">
                                  <h4 className="text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                                    📋 Rapport d'Audit
                                  </h4>
                                  {/* Audit Summary */}
                                  <div className="grid grid-cols-4 gap-2 mb-3">
                                    <div className="bg-white rounded p-2 border border-slate-200 text-center">
                                      <p className="text-lg font-bold text-blue-600">{sync.details.audit.summary?.fields_changed || 0}</p>
                                      <p className="text-[10px] text-slate-500">Champs modifiés</p>
                                    </div>
                                    <div className="bg-white rounded p-2 border border-slate-200 text-center">
                                      <p className="text-lg font-bold text-emerald-600">{sync.details.audit.summary?.empty_to_filled || 0}</p>
                                      <p className="text-[10px] text-slate-500">Vides → Remplis</p>
                                    </div>
                                    <div className="bg-white rounded p-2 border border-slate-200 text-center">
                                      <p className="text-lg font-bold text-slate-400">{sync.details.audit.summary?.unchanged || 0}</p>
                                      <p className="text-[10px] text-slate-500">Inchangés</p>
                                    </div>
                                    <div className="bg-white rounded p-2 border border-slate-200 text-center">
                                      <p className="text-lg font-bold text-red-600">{sync.details.audit.summary?.failed_validations || 0}</p>
                                      <p className="text-[10px] text-slate-500">Échecs validation</p>
                                    </div>
                                  </div>
                                  {/* Empty Fields Filled */}
                                  {sync.details.audit.empty_fields_filled?.length > 0 && (
                                    <details className="mb-2">
                                      <summary className="text-xs font-medium text-emerald-700 cursor-pointer hover:text-emerald-800">
                                        ✅ Champs vides remplis ({sync.details.audit.empty_fields_filled.length})
                                      </summary>
                                      <div className="mt-2 bg-white rounded border border-emerald-200 p-2 max-h-48 overflow-y-auto">
                                        <table className="w-full text-[11px]">
                                          <thead>
                                            <tr className="border-b text-left">
                                              <th className="py-1 text-slate-500">Record</th>
                                              <th className="py-1 text-slate-500">Champ</th>
                                              <th className="py-1 text-slate-500">Nouvelle valeur</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {sync.details.audit.empty_fields_filled.slice(0, 20).map((item, idx) => (
                                              <tr key={idx} className="border-b border-slate-100">
                                                <td className="py-1 font-mono text-slate-600">{item.matching_key || item.record_id}</td>
                                                <td className="py-1 text-slate-700">{item.field_label}</td>
                                                <td className="py-1 font-mono text-emerald-700">{item.after}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                        {sync.details.audit.empty_fields_filled.length > 20 && (
                                          <p className="text-[10px] text-slate-400 mt-1">... et {sync.details.audit.empty_fields_filled.length - 20} autres</p>
                                        )}
                                      </div>
                                    </details>
                                  )}
                                  {/* Changes Applied */}
                                  {sync.details.audit.changes_applied?.length > 0 && (
                                    <details className="mb-2">
                                      <summary className="text-xs font-medium text-blue-700 cursor-pointer hover:text-blue-800">
                                        🔄 Modifications appliquées ({sync.details.audit.changes_applied.length})
                                      </summary>
                                      <div className="mt-2 bg-white rounded border border-blue-200 p-2 max-h-48 overflow-y-auto">
                                        <table className="w-full text-[11px]">
                                          <thead>
                                            <tr className="border-b text-left">
                                              <th className="py-1 text-slate-500">Record</th>
                                              <th className="py-1 text-slate-500">Champ</th>
                                              <th className="py-1 text-slate-500">Avant</th>
                                              <th className="py-1 text-slate-500">Après</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {sync.details.audit.changes_applied.slice(0, 20).map((item, idx) => (
                                              <tr key={idx} className="border-b border-slate-100">
                                                <td className="py-1 font-mono text-slate-600">{item.matching_key || item.record_id}</td>
                                                <td className="py-1 text-slate-700">{item.field_label}</td>
                                                <td className="py-1 font-mono text-red-600 line-through">{item.before || "(vide)"}</td>
                                                <td className="py-1 font-mono text-emerald-700">{item.after}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                        {sync.details.audit.changes_applied.length > 20 && (
                                          <p className="text-[10px] text-slate-400 mt-1">... et {sync.details.audit.changes_applied.length - 20} autres</p>
                                        )}
                                      </div>
                                    </details>
                                  )}
                                  {/* Validation Failures */}
                                  {sync.details.audit.validation_failures?.length > 0 && (
                                    <details className="mb-2">
                                      <summary className="text-xs font-medium text-amber-700 cursor-pointer hover:text-amber-800">
                                        ⚠️ Échecs de validation ({sync.details.audit.validation_failures.length})
                                      </summary>
                                      <div className="mt-2 bg-white rounded border border-amber-200 p-2 max-h-48 overflow-y-auto">
                                        {sync.details.audit.validation_failures.slice(0, 15).map((item, idx) => (
                                          <div key={idx} className="py-1.5 border-b border-slate-100 last:border-0">
                                            <div className="flex items-center gap-2">
                                              <span className="font-mono text-slate-600 text-[11px]">{item.matching_key || item.record_id}</span>
                                              {item.field_label && <span className="text-[11px] text-slate-500">→ {item.field_label}</span>}
                                            </div>
                                            <p className="text-[11px] text-amber-700 mt-0.5">{item.reason}</p>
                                            {item.expected && (
                                              <p className="text-[10px] text-slate-500">
                                                Attendu: <span className="font-mono">{item.expected}</span> |
                                                Reçu: <span className="font-mono">{item.actual}</span>
                                              </p>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  )}
                                  {/* Errors with Causes */}
                                  {sync.details.audit.errors_with_causes?.length > 0 && (
                                    <details className="mb-2" open>
                                      <summary className="text-xs font-medium text-red-700 cursor-pointer hover:text-red-800">
                                        ❌ Erreurs avec diagnostic ({sync.details.audit.errors_with_causes.length})
                                      </summary>
                                      <div className="mt-2 space-y-2">
                                        {sync.details.audit.errors_with_causes.slice(0, 10).map((item, idx) => (
                                          <div key={idx} className="bg-white rounded border border-red-200 p-2">
                                            <div className="flex items-center justify-between mb-1">
                                              <span className="font-mono text-slate-700 text-[11px] font-medium">{item.matching_key || item.record_id}</span>
                                              <span className="text-[10px] text-slate-400">{item.direction}</span>
                                            </div>
                                            <p className="text-[11px] text-red-700 font-medium mb-1">🔍 Cause: {item.cause}</p>
                                            {item.error_messages?.length > 0 && (
                                              <div className="bg-red-50 rounded p-1.5 mt-1">
                                                {item.error_messages.map((msg, msgIdx) => (
                                                  <p key={msgIdx} className="text-[10px] font-mono text-red-600 break-all">{msg}</p>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  )}
                                </div>
                              )}
                              {/* Debug log */}
                              {sync.details?.debug_log?.length > 0 && (
                                <details className="mt-2">
                                  <summary className="text-xs font-medium text-slate-500 cursor-pointer hover:text-slate-700">
                                    Debug log ({sync.details.debug_log.length} entrées)
                                  </summary>
                                  <div className="mt-1 bg-slate-900 text-green-400 rounded-lg p-3 max-h-60 overflow-y-auto font-mono text-[11px] leading-relaxed">
                                    {sync.details.debug_log.map((line, j) => (
                                      <div key={j} className={
                                        line.includes("[ERROR]") || line.includes("[TRACEBACK]") ? "text-red-400" :
                                        line.includes("[SWITCH]") || line.includes("[COVERAGE]") ? "text-yellow-300" :
                                        line.includes("[RESULT]") ? "text-cyan-300" :
                                        line.includes("[SAMPLE]") ? "text-purple-300" :
                                        "text-green-400"
                                      }>{line}</div>
                                    ))}
                                  </div>
                                </details>
                              )}
                              {/* Record details with before/after */}
                              {sync.details?.record_details?.length > 0 && (
                                <details className="mt-2">
                                  <summary className="text-xs font-medium text-slate-500 cursor-pointer hover:text-slate-700">
                                    📊 Détail des enregistrements ({sync.details.record_details.length} premiers)
                                  </summary>
                                  <div className="mt-2 space-y-2 max-h-96 overflow-y-auto">
                                    {sync.details.record_details.map((rec, j) => (
                                      <div key={j} className={`p-3 rounded-lg border text-xs ${
                                        rec.status === "created" ? "bg-emerald-50 border-emerald-200" :
                                        rec.status === "updated" ? "bg-blue-50 border-blue-200" :
                                        rec.status === "error" ? "bg-red-50 border-red-200" :
                                        "bg-slate-50 border-slate-200"
                                      }`}>
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex items-center gap-2">
                                            <span className="font-semibold">{rec.matching_key || `HS:${rec.hs_id}`}</span>
                                            <Badge variant="outline" className={`text-[10px] ${
                                              rec.status === "created" ? "bg-emerald-100 text-emerald-700" :
                                              rec.status === "updated" ? "bg-blue-100 text-blue-700" :
                                              rec.status === "error" ? "bg-red-100 text-red-700" :
                                              "bg-slate-100 text-slate-600"
                                            }`}>
                                              {rec.status === "created" ? "Créé" :
                                               rec.status === "updated" ? "Mis à jour" :
                                               rec.status === "error" ? "Erreur" :
                                               rec.status === "skipped" ? "Ignoré" : rec.status}
                                            </Badge>
                                            {rec.direction && <span className="text-slate-400">{rec.direction}</span>}
                                          </div>
                                          <div className="text-slate-400 font-mono">
                                            {rec.bc_id && <span>BC: {rec.bc_id.substring(0, 8)}...</span>}
                                          </div>
                                        </div>
                                        {rec.reason && (
                                          <p className="text-slate-600 mb-2">{rec.reason}</p>
                                        )}
                                        {rec.errors?.length > 0 && (
                                          <div className="text-red-600 mb-2">
                                            {rec.errors.map((err, k) => <p key={k} className="break-all">{err}</p>)}
                                          </div>
                                        )}
                                        {/* Payload sent */}
                                        {rec.payload_sent && Object.keys(rec.payload_sent).length > 0 && (
                                          <details className="mt-2">
                                            <summary className="text-[11px] font-medium text-slate-500 cursor-pointer">
                                              📤 Payload envoyé ({Object.keys(rec.payload_sent).length} champs)
                                            </summary>
                                            <div className="mt-1 bg-white rounded p-2 border border-slate-200">
                                              <table className="w-full text-[11px]">
                                                <thead>
                                                  <tr className="border-b">
                                                    <th className="text-left py-1 text-slate-500">Champ</th>
                                                    <th className="text-left py-1 text-slate-500">Valeur envoyée</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {Object.entries(rec.payload_sent).map(([k, v]) => (
                                                    <tr key={k} className="border-b border-slate-100">
                                                      <td className="py-1 font-mono text-slate-600">{k}</td>
                                                      <td className="py-1 font-mono text-emerald-700">{String(v)}</td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          </details>
                                        )}
                                        {/* Before/After comparison */}
                                        {rec.bc_before && Object.keys(rec.bc_before).length > 0 && (
                                          <details className="mt-2" open>
                                            <summary className="text-[11px] font-medium text-slate-500 cursor-pointer">
                                              🔄 Avant / Après (BC record)
                                            </summary>
                                            <div className="mt-1 bg-white rounded p-2 border border-slate-200 overflow-x-auto">
                                              <table className="w-full text-[11px]">
                                                <thead>
                                                  <tr className="border-b">
                                                    <th className="text-left py-1 text-slate-500">Champ</th>
                                                    <th className="text-left py-1 text-slate-500">Avant (BC)</th>
                                                    <th className="text-left py-1 text-slate-500">→ Envoyé</th>
                                                    <th className="text-left py-1 text-slate-500">Après (BC)</th>
                                                    <th className="text-left py-1 text-slate-500">Statut</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {Object.entries(rec.payload_sent || {}).map(([k, v]) => {
                                                    const beforeVal = rec.bc_before?.[k];
                                                    const afterVal = rec.bc_after?.[k];
                                                    const sentVal = String(v ?? "");

                                                    // Determine if before was empty
                                                    const beforeEmpty = beforeVal === null || beforeVal === undefined || String(beforeVal).trim() === "";
                                                    const afterEmpty = afterVal === null || afterVal === undefined || String(afterVal).trim() === "";
                                                    const sentEmpty = sentVal.trim() === "";

                                                    const beforeStr = beforeEmpty ? "" : String(beforeVal);
                                                    const afterStr = afterEmpty ? "" : String(afterVal);

                                                    // Determine what happened
                                                    const wasAdded = beforeEmpty && !sentEmpty;
                                                    const wasChanged = !beforeEmpty && !sentEmpty && beforeStr !== sentVal;
                                                    const wasCleared = !beforeEmpty && sentEmpty;
                                                    const noChange = beforeStr === sentVal;

                                                    // Check if update succeeded
                                                    const success = afterStr === sentVal || (sentEmpty && afterEmpty);

                                                    // Row styling
                                                    let rowClass = "border-b border-slate-100";
                                                    if (wasAdded) rowClass += success ? " bg-emerald-50" : " bg-yellow-50";
                                                    else if (wasChanged) rowClass += success ? " bg-blue-50" : " bg-yellow-50";
                                                    else if (wasCleared) rowClass += " bg-slate-50";

                                                    return (
                                                      <tr key={k} className={rowClass}>
                                                        <td className="py-1.5 font-mono text-slate-700 font-medium">{k}</td>
                                                        <td className="py-1.5 font-mono">
                                                          {beforeEmpty ? (
                                                            <span className="text-slate-300 italic">(vide)</span>
                                                          ) : (
                                                            <span className="text-slate-600">{beforeStr}</span>
                                                          )}
                                                        </td>
                                                        <td className="py-1.5 font-mono">
                                                          {sentEmpty ? (
                                                            <span className="text-slate-300 italic">(vide)</span>
                                                          ) : (
                                                            <span className="text-indigo-600 font-semibold">{sentVal}</span>
                                                          )}
                                                        </td>
                                                        <td className="py-1.5 font-mono">
                                                          {afterVal === undefined ? (
                                                            <span className="text-slate-300 italic">?</span>
                                                          ) : afterEmpty ? (
                                                            <span className="text-slate-300 italic">(vide)</span>
                                                          ) : (
                                                            <span className={success ? "text-emerald-700 font-semibold" : "text-orange-600"}>{afterStr}</span>
                                                          )}
                                                        </td>
                                                        <td className="py-1.5">
                                                          {noChange ? (
                                                            <span className="text-slate-400 text-[10px]">Inchangé</span>
                                                          ) : wasAdded && success ? (
                                                            <span className="text-emerald-600 font-medium">✓ Ajouté</span>
                                                          ) : wasChanged && success ? (
                                                            <span className="text-blue-600 font-medium">✓ Modifié</span>
                                                          ) : wasCleared && success ? (
                                                            <span className="text-slate-500">✓ Vidé</span>
                                                          ) : afterVal === undefined ? (
                                                            <span className="text-slate-400">?</span>
                                                          ) : (
                                                            <span className="text-orange-600 font-medium">⚠ Échec</span>
                                                          )}
                                                        </td>
                                                      </tr>
                                                    );
                                                  })}
                                                </tbody>
                                              </table>
                                            </div>
                                          </details>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Aucune synchronisation dans l'historique</p>
              <p className="text-sm text-slate-400 mt-1">
                Les synchronisations apparaîtront ici après leur exécution
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
